import { z } from 'zod';
import { DateSchema, ObjectIdHexStringSchema, ObjectIdSchema } from './base';
import { PRICING_VERSIONS } from './billing/PRICING_VERSIONS';
import { EntityEventType } from './entityEvents';
import { TagsCollectionSchema, TagsSchema } from './starters';
import { UnitType } from './units';

export enum EventTypes {
  REGULAR = 'REGULAR',
  CONTEST = 'CONTEST',
  CONTEST_GROUP = 'CONTEST_GROUP',
  STAGE = 'STAGE',
  STAGE_GROUP = 'STAGE_GROUP',
  SAFEMAP = 'SAFEMAP',
}

export enum AuthorizationStates {
  NONE = 'NONE',
  PAID = 'PAID',
  PAID_FREE_LOADINGS = 'PAID_FREE_LOADINGS',
  FREE = 'FREE',
}

export const EventOverviewSchema = z.object({
  id: ObjectIdSchema,
  name: z.string(),
  startTime: DateSchema,
  endTime: DateSchema,
  isRepeating: z.boolean(),
  isPredictive: z.boolean(),
  updatedAt: DateSchema,
  geo: z.object({
    shadowgeojsonHash: z.string().optional(),
  }),
  type: z.enum(EventTypes),
});

export type EventOverview = z.infer<typeof EventOverviewSchema>;

export const EventOverviewListSchema = z.array(EventOverviewSchema);

export type EventOverviewList = z.infer<typeof EventOverviewListSchema>;

export enum EventLoadTypes {
  MAP_LOADS = 'MAP',
  TIMING_LOADS = 'TIMING',
  MONITOR_LOADS = 'MONITOR',
  CURRENT_LOADS = 'CURRENT_API',
  TIMES_LOADS = 'TIMES_API',
  TIMES_ID_LOADS = 'TIMES_ID_API',
  RANKS_LOADS = 'RANKS_API',
  DISTANCE_LOADS = 'DISTANCE_API',
}

export enum SportTypes {
  RUNNING = 'running',
  TRAIL = 'trail',
  BIKING = 'biking',
  MTB = 'mtb',
  INLINE_SKATING = 'inline-skating',
  SWIMMING = 'swimming',
  TRIATHLON = 'triathlon',
  EQUESTRIAN_SPORT = 'equestrian',
  ORIENTEERING = 'orienteering',
  ROWING = 'rowing',
  SAILING = 'sailing',
  CANOEING = 'canoeing',
  MOTORBOAT = 'motorboat',
  CYCLING = 'cycling',
  SUP = 'sup',
  SKIING = 'skiing',
  MOTOCROSS = 'motocross',
  RALLY_CAR = 'rally-car',
  HIKING = 'hiking',
  DUATHLON = 'duathlon',
  TRANSITION_AREA = 'transition_area',
  OTHER = 'other',
}

export enum VisibilityStates {
  DRAFT = 'DRAFT',
  UNLISTED = 'UNLISTED',
  LISTED = 'LISTED',
  PUBLIC = 'PUBLIC',
  ARCHIVED = 'ARCHIVED',
}

export enum MapType {
  SATELLITE = 'SATELLITE',
  TERRAIN = 'TERRAIN',
  HYBRID = 'HYBRID',
  ROADMAP = 'ROADMAP',
  DARK = 'DARK',
}

export enum FlagContent {
  STARTNUMBER = 'STARTNUMBER',
  NAME = 'NAME',
  STARTNUMBERANDNAME = 'STARTNUMBER_AND_NAME',
}

export enum PinToShadowTrackMode {
  NONE = 'NONE',
  MAGNETIC = 'MAGNETIC',
  FORCED = 'FORCED',
}

export enum TimeMode {
  RELATIVE = 'RELATIVE',
  ABSOLUTE = 'ABSOLUTE',
}

export enum AppScreenKeys {
  PLAYER = 'PLAYER',
  LEADERBOARD = 'LEADERBOARD',
}

export enum RRImportMode {
  EVENT_ID = 'EVENT_ID',
  CUSTOM_URL = 'CUSTOM_URL',
}

export enum RRLeaderboardType {
  RESULTS = 'results',
  LIVE = 'live',
  REGISTRATION = 'registration',
}

export enum TimingSystems {
  NONE = 'NONE',
  OTHER = 'OTHER',
  FEIBOT = 'FEIBOT',
  MY_LAPS = 'MY_LAPS',
  RACERESULT = 'RACERESULT',
  CHRONO_TRACK = 'CHRONO_TRACK',
}

export enum ModulesStates {
  NONE = 'NONE',
  /** @deprecated modules should only be activated or not */
  ENABLED = 'ENABLED',
  ACTIVATED = 'ACTIVATED',
}

export enum LeaderboardType {
  DEFAULT = 'DEFAULT',
  EXTERNAL = 'EXTERNAL',
  RACE_RESULT = 'RACE_RESULT',
}

export enum LeaderboardHeaderContent {
  NAME = 'NAME',
  ICON = 'ICON',
  DISTANCE = 'DISTANCE',
}

export enum RegistrationType {
  EXTERNAL = 'EXTERNAL',
  RACE_RESULT = 'RACE_RESULT',
}

export const EventTypeSchema = z.object({
  id: z.enum(SportTypes),
  name: z.string(),
  description: z.string(),
  symbol: z.string(),
  defaultSpeed: z.number(),
  maxSpeed: z.number(),
  minSpeed: z.number(),
  onlySplit: z.boolean(),
});

export type EventType = z.infer<typeof EventTypeSchema>;

export const PointWithOffsetSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  offset: z.number(),
  timestamp: DateSchema,
});
export type PointWithOffset = z.infer<typeof PointWithOffsetSchema>;

export const StarterPointsSchema = z.array(PointWithOffsetSchema);
export type StarterPoints = z.infer<typeof StarterPointsSchema>;

export const EventPointsSchema = z
  .union([
    z.map(ObjectIdHexStringSchema, StarterPointsSchema),
    z.record(z.string(), StarterPointsSchema),
  ])
  .transform((v) => {
    if (v instanceof Map) {
      return v;
    }

    return new Map(
      Object.entries(v).map(([key, value]) => [ObjectIdHexStringSchema.parse(key), value]),
    );
  });
export type EventPoints = z.infer<typeof EventPointsSchema>;

export const ImportStarterCheckResultSchema = z.object({
  success: z.boolean(),
  importId: z.coerce.string(),
  name: z.string(),
  tags: TagsSchema,
  errors: z.array(z.string()),
});

export type ImportStarterCheckResult = z.infer<typeof ImportStarterCheckResultSchema>;

export const GenericImportCheckResultSchema = z.object({
  errors: z.array(z.string()),
  failures: z.array(ImportStarterCheckResultSchema),
  validCount: z.number(),
  possibleTags: TagsCollectionSchema,
});

export type GenericImportCheckResult = z.infer<typeof GenericImportCheckResultSchema>;

const StartersSyncOptionsSchema = z.object({
  deleteAbandonedStarters: z.boolean(),
  lastProblemReportSentAt: DateSchema.nullable(),
});

export type StartersSyncOptions = z.infer<typeof StartersSyncOptionsSchema>;

const ImagesSchema = z.looseObject({
  fileName: z.string(),
  original: z.string(),
  app: z.string().optional(),
  large: z.string().optional(),
  medium: z.string().optional(),
  small: z.string().optional(),
  property: z.string().optional(),
  icon: z.string().optional(),
});

export type Images = z.infer<typeof ImagesSchema>;

const PredictiveParamsSchema = z.object({
  // Parameter influencing the mapping
  // A reader location must be within this distance to the track
  distanceFromTrack: z.number(), // meters
  // Parameter influencing the interpolation allgorithm
  // the max speed in m/s that is used to correct faulty predictions in live mode
  maxCompensationSpeed: z.number(), // meters / second
  // minimum distance before we accept a new valid ping position -> bigger number decreases speed measuring errors
  minDistance: z.number(), // meters
  // all Pings below this value will be ignored
  rssiThreshold: z.number(), // dBm
  // At this point in future the simulated starter should meet the measured starter
  futureTrackScaleValue: z.number(),
  // We use this distance in meters to measure the speed of a starter for approximation
  lookBackDistance: z.number(), // meters
  // we use this time interval to look into the future. In live mode we let the starters run lookForwardTimeInMs into the future. This should compensate for latency issues rising when pushing data through our system until it arrives in the player
  lookForwardTime: z.number(), // seconds
  // when the live prediction is to far away (timewise) from the last measured position we jump to the last measured position
  // we do a jump forward or backwards if the time difference is bigger than jumpingDotThresholdInMs
  jumpingDotThreshold: z.number(), // seconds
});

export type PredictionParams = z.infer<typeof PredictiveParamsSchema>;

const SponsorLogoSchema = z.object({
  id: z.string(),
  url: z.string(),
  title: z.string(),
  imageUrls: ImagesSchema.nullable(),
  altText: z.string(),
});

export type SponsorLogo = z.infer<typeof SponsorLogoSchema>;

const SponsorModuleSchema = z.object({
  state: z.enum(ModulesStates),
  alignment: z.enum(['left', 'center', 'right', 'space-between', 'space-around']),
  logos: z.array(SponsorLogoSchema).nullable(),
});

export type SponsorModule = z.infer<typeof SponsorModuleSchema>;

const PredictiveModuleSchema = z.object({
  state: z.undefined(),
  enabled: z.boolean(),
  timingsystem: z.enum(TimingSystems),
  params: PredictiveParamsSchema,
});

const KeysModuleSchema = z.object({
  enabled: z.boolean(),
  state: z.undefined(),
});

const LeaderboardOptionsSchema = z.object({
  externalLeaderboardUrl: z.string(),
  type: z.enum(LeaderboardType),
  headerColor: z.string(),
});

const TeamTargetSchema = z.object({
  enabled: z.boolean(),
  distance: z.number(),
  description: z.string(),
});

export type TeamTarget = z.infer<typeof TeamTargetSchema>;

const VirtualRaceVisibleElementsSchema = z.object({
  difference: z.boolean(),
  activeDuration: z.boolean(),
  restDuration: z.boolean(),
  liveState: z.boolean(),
  currentProgress: z.boolean(),
  finish: z.boolean(),
  stages: z.boolean(),
  rank: z.boolean(),
  eventTimes: z.boolean(),
  mapLink: z.boolean(),
  distanceInHeader: z.boolean(),
  participateAfterEvent: z.boolean(),
  headerContent: z.array(z.enum(LeaderboardHeaderContent)),
});

const TimingModuleSchema = z.object({
  leaderboard: LeaderboardOptionsSchema,
  state: z.enum(ModulesStates),
  enabled: z.undefined(),
  predefinedTrack: z.boolean(),
  distance: z.number(),
  duration: z.number(),
  target: z.string(),
  teamTarget: TeamTargetSchema,
  liveRanking: z.boolean(),
  visibleElements: VirtualRaceVisibleElementsSchema,
});

const DataFeedModuleSchema = z.object({
  enabled: z.undefined(),
  state: z.enum(ModulesStates),
  autoPausedAt: DateSchema.nullable(),
  geofenceRange: z.number(),
});

const NetReplayModuleSchema = z.object({
  state: z.undefined(),
  enabled: z.boolean(),
  splitId: ObjectIdSchema.nullable(),
  sliderDuration: z.number(),
});

const ProjectionModuleSchema = z.object({
  enabled: z.boolean(),
  state: z.undefined(),
});

const RegistrationModuleSchema = z.object({
  enabled: z.undefined(),
  state: z.undefined(),
  type: z.enum(RegistrationType),
  rrRegistrationKey: z.string(),
  externalRegistrationUrl: z.string(),
  rrRegistrationFormName: z.string(),
});

const MonitorModuleSchema = z.object({
  enabled: z.undefined(),
  state: z.enum(ModulesStates),
  alertTargetMails: z.array(z.object({ email: z.string(), lang: z.enum(['en']) })).optional(),
});

const BannerModuleSchema = z.object({
  imageUrls: ImagesSchema.nullable(),
  enabled: z.undefined(),
  state: z.undefined(),
});

const OptionModulesSchema = z.object({
  predictive: PredictiveModuleSchema,
  sponsor: SponsorModuleSchema,
  keys: KeysModuleSchema,
  timing: TimingModuleSchema,
  dataFeed: DataFeedModuleSchema,
  netReplay: NetReplayModuleSchema,
  projection: ProjectionModuleSchema,
  brandedAppIds: z.array(ObjectIdSchema),
  registration: RegistrationModuleSchema,
  monitor: MonitorModuleSchema,
  banner: BannerModuleSchema,
});

export type OptionModules = z.infer<typeof OptionModulesSchema>;

const PaymentsSchema = z.object({
  invariants: z.object({
    pricingVersion: z.enum(PRICING_VERSIONS),
    isReseller: z.boolean(),
  }),
});

export type Payments = z.infer<typeof PaymentsSchema>;

const GeosSchema = z.object({
  shadowgeojsonHash: z.string().optional(),
  shadowtrackId: ObjectIdSchema.nullable(),
  geosUpdatedAt: DateSchema,
});

export type Geos = z.infer<typeof GeosSchema>;

const AlertSuppressionTagsSchema = z.array(z.string());

export type AlertSuppressionTags = z.infer<typeof AlertSuppressionTagsSchema>;

const AlertOptionsSchema = z.object({
  noMovementDuration: z.number(),
  autoResolveAlerts: z.array(z.enum(EntityEventType)),
  generateGeoBasedAlerts: z.array(
    z.enum([EntityEventType.AWAY_FROM_TRACK, EntityEventType.NO_MOVEMENT]),
  ),
  alertSuppressionTags: AlertSuppressionTagsSchema.optional(),
  muteSounds: z.boolean(),
});

const InfoTabsSchema = z.object({
  openByDefault: z.boolean(),
  defaultTab: z.string(),
  disabledTabs: z.array(z.string()),
});

export type InfoTabs = z.infer<typeof InfoTabsSchema>;

const ViewportSchema = z.object({
  longitude: z.number(),
  latitude: z.number(),
  zoom: z.number(),
  pitch: z.number(),
  bearing: z.number(),
  padding: z.object({
    top: z.number(),
    bottom: z.number(),
    left: z.number(),
    right: z.number(),
  }),
});

export type ViewState = z.infer<typeof ViewportSchema>;
export type Viewport = z.infer<typeof ViewportSchema>;

const ElevationChartOptionsSchema = z.object({
  min: z.number().nullable(),
  max: z.number().nullable(),
});

export type ElevationChartOptions = z.infer<typeof ElevationChartOptionsSchema>;

export const TagCategoriesConfigEntrySchema = z.object({
  visible: z.boolean(),
  default: z.array(z.string()),
  groupStarterBy: z.boolean(),
  index: z.number(),
});

export type TagCategoriesConfigEntry = z.infer<typeof TagCategoriesConfigEntrySchema>;

export const TagCategoriesConfigSchema = z.record(z.string(), TagCategoriesConfigEntrySchema);

export type TagCategoriesConfig = z.infer<typeof TagCategoriesConfigSchema>;

const PlayerOptionsSchema = z.object({
  stepWidth: z.number(),
  markerTimeout: z.number(),
  baseColor: z.string(),
  breakTimeout: z.number(),
  replaySpeedup: z.number(),
  liveDelay: z.number(),
  pollingInterval: z.number(),
  mapType: z.enum(MapType),
  unitType: z.enum(UnitType),
  flagContent: z.enum(FlagContent),
  showAllFlags: z.boolean(),
  showDistanceMarkers: z.boolean(),
  infoTabs: InfoTabsSchema,
  pinToShadowTrack: z.enum(PinToShadowTrackMode),
  version: z.string(),
  viewport: ViewportSchema.nullable(),
  elevationChartOptions: ElevationChartOptionsSchema,
  listOpenByDefault: z.boolean(),
  mappingDistance: z.number(),
  timeMode: z.enum(TimeMode),
  tailTimeout: z.number(),
  reloadInterval: z.number(),
  tagCategoriesConfig: TagCategoriesConfigSchema,
  largeMode: z.boolean(),
});

export type PlayerOptions = z.infer<typeof PlayerOptionsSchema>;

const AppOptionsSchema = z.object({
  screens: z.object({
    defaultScreen: z.enum(AppScreenKeys),
    disabledScreens: z.array(z.enum(AppScreenKeys)),
  }),
  customLinkButton: z.object({
    caption: z.string(),
    url: z.string(),
  }),
});

export type AppOptions = z.infer<typeof AppOptionsSchema>;

const RRCustomerIdsSchema = z.array(z.string());
export type RRCustomerIds = z.infer<typeof RRCustomerIdsSchema>;

const RREventIdSchema = z.string();
export type RREventId = z.infer<typeof RREventIdSchema>;

const RRContestsSchema = z.array(z.string());
export type RRContests = z.infer<typeof RRContestsSchema>;

const IntegrationsSchema = z.object({
  raceresult: z.object({
    isActive: z.boolean(),
    eventId: RREventIdSchema,
    contests: RRContestsSchema,
    customerIds: RRCustomerIdsSchema,
    starterListUrl: z.string(),
    importMode: z.enum(RRImportMode),
    lastImportAt: DateSchema.nullable(),
    leaderboardType: z.enum(RRLeaderboardType),
  }),
  feibot: z.object({
    eventId: z.string().nullable(),
  }),
  genericStarterImport: z.object({
    isActive: z.boolean(),
    sources: z.array(
      z.object({
        sourceURL: z.string(),
        filter: z
          .object({
            tagKey: z.string(),
            tagValues: z.array(z.string()),
          })
          .nullable(),
      }),
    ),
    lastImportAt: DateSchema.nullable(),
  }),
});

export const EventSchema = z.object({
  id: ObjectIdSchema,
  creatorId: ObjectIdSchema,
  editorIds: z.array(ObjectIdSchema),
  createdAt: DateSchema,
  updaterId: ObjectIdSchema,
  updatedAt: DateSchema,

  copyOf: ObjectIdSchema.nullable(),
  paidAt: DateSchema.nullable(),

  name: z.string(),
  slug: z.string().optional(),
  type: z.enum(EventTypes),
  eventType: z.enum(SportTypes).nullable(),
  maxSpeed: z.number(),
  minSpeed: z.number(),
  startTime: DateSchema,
  endTime: DateSchema,
  location: z.string(),
  isRepeating: z.boolean(),
  defaultSpeed: z.number(),

  index: z.number().nullable(), // only relevant for stages and groups

  // Meta informations mostly used in the app and the player
  eventWebsite: z.string(),
  eventDescription: z.string(),
  eventContactMail: z.string(),
  eventContactPhone: z.string(),
  eventTechnicalSupportMail: z.string(),

  starterIds: z.array(ObjectIdSchema).optional(),
  startersCount: z.number(),
  startersSyncOptions: StartersSyncOptionsSchema,
  images: ImagesSchema.nullable(),
  visibility: z.enum(VisibilityStates),
  passcode: z.string(),
  isSticky: z.boolean(),
  authorization: z.enum(AuthorizationStates),
  parent: ObjectIdSchema.nullable(),

  modules: OptionModulesSchema,
  payments: PaymentsSchema,
  geo: GeosSchema,

  playerOptions: PlayerOptionsSchema,
  appOptions: AppOptionsSchema,
  integrations: IntegrationsSchema,

  startTimeOrderingKey: DateSchema,
  alertOptions: AlertOptionsSchema,
  isPublicSafemap: z.boolean(),
});

// TODO: we use that type for backend and frontend, but some fields only exist in the backend and are optional in frontend.
// We should split that into a backend and frontend type to make it more clear which fields are actually available in the frontend
export type Event = z.infer<typeof EventSchema>;
